import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Trash2, Wand2, ImageIcon, Loader2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [autoImage, setAutoImage] = useState(null); // { url, source }
  const [generating, setGenerating] = useState(false);

  const load = () => api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const autoGenerate = async () => {
    if (!name.trim()) {
      toast.error("Type a category name first");
      return;
    }
    setGenerating(true);
    setAutoImage(null);
    try {
      const { data } = await api.post("/categories/auto-image", { name: name.trim() });
      setAutoImage({ url: data.image_url, source: data.source });
      toast.success(data.source === "ai" ? "Generated with AI" : "Found a stock photo");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't find an image. Try a different name.");
    } finally {
      setGenerating(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/categories", {
        name: name.trim(),
        image_url: autoImage?.url || null,
      });
      setName("");
      setAutoImage(null);
      toast.success("Category added");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add category");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete category?")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-categories">
      <h1 className="font-display font-black text-4xl tracking-tight">Categories</h1>
      <p className="mt-2 text-sm text-neutral-600 max-w-md">
        Add a category and we'll auto-pick a matching image — AI-generated, with a stock photo fallback.
      </p>

      <form onSubmit={create} className="mt-6 max-w-md space-y-3">
        <div className="flex gap-2">
          <Input
            data-testid="category-name-input"
            value={name}
            onChange={(e) => { setName(e.target.value); setAutoImage(null); }}
            placeholder="e.g. Perfume, Watches, Skincare…"
            className="rounded-none border-black h-11"
          />
          <Button
            data-testid="category-autoimage-btn"
            type="button"
            disabled={generating || !name.trim()}
            onClick={autoGenerate}
            title="Auto-pick an image with AI"
            className="rounded-none bg-white text-black border border-black hover:bg-[#FFE600] hover:border-black h-11 px-3 disabled:opacity-50"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Wand2 className="w-4 h-4" />
            )}
          </Button>
        </div>

        {/* Preview / placeholder */}
        <div
          data-testid="autoimage-preview"
          className="aspect-square w-full max-w-[220px] border border-dashed border-neutral-300 flex items-center justify-center bg-neutral-50 relative"
        >
          {generating ? (
            <div className="flex flex-col items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest">
              <Loader2 className="w-6 h-6 animate-spin" />
              Generating…
            </div>
          ) : autoImage ? (
            <>
              <img
                src={autoImage.url}
                alt={name}
                className="w-full h-full object-cover"
              />
              <span
                data-testid="autoimage-source"
                className={`absolute top-1 left-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 ${
                  autoImage.source === "ai" ? "bg-[#FF2A00] text-white" : "bg-black text-white"
                }`}
              >
                {autoImage.source === "ai" ? "AI" : "Stock"}
              </span>
              <button
                type="button"
                onClick={() => setAutoImage(null)}
                title="Clear preview"
                className="absolute top-1 right-1 w-6 h-6 bg-white border border-black flex items-center justify-center hover:bg-neutral-100"
              >
                <X className="w-3 h-3" />
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 text-neutral-400 text-[10px] uppercase tracking-widest text-center px-2">
              <ImageIcon className="w-6 h-6" />
              No image yet · click <Wand2 className="w-3 h-3 inline-block" /> to auto-pick
            </div>
          )}
        </div>

        <Button
          data-testid="add-category-btn"
          type="submit"
          disabled={generating}
          className="rounded-none bg-[#0033FF] hover:bg-[#0022CC] h-11 w-full disabled:opacity-50"
        >
          Add category
        </Button>
      </form>

      <div className="mt-10 max-w-2xl">
        <h2 className="text-[11px] uppercase tracking-widest font-bold text-neutral-600 mb-3">
          Existing categories ({cats.length})
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {cats.map((c) => (
            <CategoryCard key={c.category_id} cat={c} onDelete={() => del(c.category_id)} onUpdate={load} />
          ))}
        </div>
      </div>
    </div>
  );
}

function CategoryCard({ cat, onDelete, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [autoImage, setAutoImage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setGenerating(true);
    setAutoImage(null);
    try {
      const { data } = await api.post("/categories/auto-image", { name: cat.name });
      setAutoImage({ url: data.image_url, source: data.source });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't find an image");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!autoImage) return;
    setBusy(true);
    try {
      await api.put(`/categories/${cat.category_id}`, { image_url: autoImage.url });
      toast.success("Image updated");
      setEditing(false);
      setAutoImage(null);
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const previewUrl = autoImage?.url || cat.image_url;

  return (
    <div data-testid={`admin-category-${cat.slug}`} className="border border-neutral-200">
      <div className="aspect-square bg-neutral-50 relative overflow-hidden">
        {previewUrl ? (
          <img src={previewUrl} alt={cat.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-300">
            <ImageIcon className="w-8 h-8" />
          </div>
        )}
        {autoImage && (
          <span
            className={`absolute top-1 left-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 ${
              autoImage.source === "ai" ? "bg-[#FF2A00] text-white" : "bg-black text-white"
            }`}
          >
            {autoImage.source === "ai" ? "AI preview" : "Stock preview"}
          </span>
        )}
      </div>

      <div className="p-2 flex items-center justify-between gap-1 min-w-0">
        <span className="font-display font-bold text-sm truncate flex-1">{cat.name}</span>
        <div className="flex items-center gap-0.5 shrink-0">
          {editing && autoImage ? (
            <>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                title="Save new image"
                className="w-7 h-7 inline-flex items-center justify-center border border-black hover:bg-[#00A355] hover:text-white hover:border-[#00A355] disabled:opacity-50"
                data-testid={`save-category-img-${cat.slug}`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              </button>
              <button
                type="button"
                onClick={() => { setAutoImage(null); setEditing(false); }}
                title="Cancel"
                className="w-7 h-7 inline-flex items-center justify-center border border-black hover:bg-neutral-200"
              >
                <X className="w-3 h-3" />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => { setEditing(true); generate(); }}
                disabled={generating}
                title="Regenerate image with AI"
                className="w-7 h-7 inline-flex items-center justify-center border border-black hover:bg-[#FFE600] disabled:opacity-50"
                data-testid={`regen-category-${cat.slug}`}
              >
                {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
              </button>
              <button
                type="button"
                onClick={onDelete}
                title="Delete category"
                className="w-7 h-7 inline-flex items-center justify-center border border-black hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00]"
                data-testid={`delete-category-${cat.slug}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
